import { Button } from "@choose/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@choose/ui/empty";
import { Separator } from "@choose/ui/separator";
import { Spinner } from "@choose/ui/spinner";
import { createFileRoute, Link } from "@tanstack/react-router";
import { SearchSlashIcon, ServerCrashIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  DEFAULT_COLUMN_VISIBILITY,
  DEFAULT_TABLE_STATE,
  GroupActionList,
} from "~/components/common/group-actions/group-action-list";
import { APP_SIDEBAR_NAME } from "~/components/layout/sidebars/app-sidebar";
import { SidebarTrigger } from "~/components/ui/sidebar";
import type { Support_OrderFlow_Search_Input } from "~/graphql/graphql.gen";
import {
  Support_OrderFlow_Search_Sort_Direction,
  Support_OrderFlow_Search_Sort_Field,
  useInfiniteOrderFlowsQuery,
} from "~/graphql/graphql.gen";
import { DEFAULT_DISPLAYED_PROPERTIES } from "~/validators/search-orders-validator";

export const Route = createFileRoute(
  "/_auth/_app_shell/_main-navigation/group-actions/",
)({
  component: RouteComponent,
});

function RouteComponent() {
  const { t } = useTranslation();

  return (
    <>
      <title>{`${t("app_sidebar.group_actions")} - ${t("app_name")}`}</title>
      <div className="bg-background flex w-full flex-col md:rounded-md md:border">
        <div className="flex h-full grow flex-col">
          <header className="flex flex-col items-center">
            <div className="flex h-12 w-full items-center gap-2 border-b px-4">
              <SidebarTrigger name={APP_SIDEBAR_NAME} className="-ml-1" />
              <Separator orientation="vertical" className="mr-2" />

              <h2 className="text-sm">{t("app_sidebar.group_actions")}</h2>
              <Button
                size="sm"
                nativeButton={false}
                render={
                  <Link
                    to="/create-group-action"
                    search={{
                      displayedProperties: DEFAULT_DISPLAYED_PROPERTIES,
                      sort: "created-at-desc",
                    }}
                    className="ml-auto"
                  >
                    {t("create_group_action.title")}
                  </Link>
                }
              />
            </div>
          </header>
          <div className="h-full w-full overflow-auto">
            <GroupActions />
          </div>
        </div>
      </div>
    </>
  );
}

function GroupActions() {
  const { t } = useTranslation();

  const query: Support_OrderFlow_Search_Input = {
    first: 50,
    sortBy: {
      direction: Support_OrderFlow_Search_Sort_Direction.Desc,
      field: Support_OrderFlow_Search_Sort_Field.CreatedAt,
    },
  };

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteOrderFlowsQuery(
    {
      input: {
        ...query,
      },
    },
    {
      initialPageParam: undefined,
      getNextPageParam: (lastPage) => {
        return lastPage.support_orderFlow_search.pageInfo.hasNextPage
          ? {
              input: {
                ...query,
                after: lastPage.support_orderFlow_search.pageInfo.endCursor,
              },
            }
          : undefined;
      },
      select: (data) => data.pages.map((page) => page.support_orderFlow_search),
    },
  );

  const groupActions =
    data?.flatMap((page) => page.edges).map((edge) => edge.node) ?? [];

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty className="min-h-[300px] w-full max-w-md">
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <Spinner className="size-4" />
            </EmptyMedia>
            <EmptyTitle>{t("group_actions.loading")}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <ServerCrashIcon />
            </EmptyMedia>
            <EmptyTitle>{t("group_actions.error")}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  if (groupActions.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <SearchSlashIcon />
            </EmptyMedia>
            <EmptyTitle>{t("group_actions.empty.title")}</EmptyTitle>
            <EmptyDescription>
              {t("group_actions.empty.description")}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  return (
    <GroupActionList
      data={groupActions}
      state={{
        ...DEFAULT_TABLE_STATE,
        columnVisibility: {
          ...DEFAULT_COLUMN_VISIBILITY,
        },
      }}
      onFetchMore={() => fetchNextPage()}
      isFetchingMore={isFetchingNextPage}
      hasMore={hasNextPage}
    />
  );
}
